import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useAuth } from './AuthContext';
import { organizationsApi } from '../services/api';
import { createPermissionChecker } from '../utils/permissions';

const PermissionsContext = createContext(null);

export const usePermissionsContext = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissionsContext must be used within a PermissionsProvider');
    }
    return context;
};

export const PermissionsProvider = ({ children }) => {
    const { currentUser, userClaims } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadPermissions = useCallback(async () => {
        if (!currentUser || !userClaims?.orgId) {
            setPermissions([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const data = await organizationsApi.getMyPermissions();
            setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
        } catch (error) {
            console.error('Failed to load organization permissions:', error);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    }, [currentUser, userClaims?.orgId]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    const checker = useMemo(
        () => createPermissionChecker(permissions),
        [permissions],
    );

    const value = useMemo(() => ({
        permissions,
        loading,
        refreshPermissions: loadPermissions,
        has: checker.has,
        hasAny: checker.hasAny,
        hasAll: checker.hasAll,
    }), [permissions, loading, loadPermissions, checker]);

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};
